import React, { FC } from "react";
import { BaseFeesWithCampaignLegacy } from "./BaseFeesWithCampaignLegacy";
import { formatPrice } from "../Helpers/priceHelper";
import classNames from "classnames";
import { PriceListFeesLegacy } from "./PriceListFeesLegacy";
import { FeeWithDiscountTableLegacy } from "./FeeWithDiscountTableLegacy";
import { ServicePricingSummary } from "../../Partials/PricingHighlight/ServicePricingSummary.csharp";

interface TreatmentPricingSummaryProps extends ServicePricingSummary {
  hideHeading?: boolean;
}

export const TreatmentPricingSummary: FC<TreatmentPricingSummaryProps> = ({
  serviceName,
  baseFees,
  listWithoutPrice,
  listWithoutPriceLabel,
  basePrice,
  showWeekendEveningPrices,
  additionalFees,
  basePriceLabel,
  endPriceLabel,
  eveningWeekendPriceHeading,
  eveningWeekendSamePriceLabel,
  eveningPriceLabel,
  weekendPriceLabel,
  additionalFeesLabel,
  hideHeading = false,
  campaignPrice,
  eveningAddition,
  weekendHolidayAddition,
  eveningWeekendDiscountPriceHeading,
  highlightPriceRowOnHover,
  priceListFees,
}) => {
  const getBaseFees = () => {
    return baseFees.reduce((prev, current) => prev + current.regularPrice, 0);
  };

  const weekendAndEveningPricesAreEqualToBase =
    eveningAddition === 0 && weekendHolidayAddition === 0;
  const weekendAndEveningPricesAreEqual =
    eveningAddition === weekendHolidayAddition &&
    !weekendAndEveningPricesAreEqualToBase &&
    !!eveningAddition;

  return (
    <>
      {!hideHeading && (
        <summary
          className={classNames(
            "details__summary theme-inherit-light p-2 mb-0 pricing-details-summary",
            {
              "campaign-byline-after":
                !!campaignPrice?.pricingServiceNameByline,
            }
          )}
        >
          <span className="pe-1">
            {campaignPrice?.pricingServiceNameByline && (
              <div className="small-text fw-normal campaign-byline">
                {campaignPrice?.pricingServiceNameByline}
              </div>
            )}
            {campaignPrice?.pricingServiceName ?? serviceName}
          </span>
          <span
            className={classNames({
              "campaign-byline-price":
                !!campaignPrice?.pricingServiceNameByline,
            })}
          >
            {listWithoutPrice && listWithoutPriceLabel && (
              <span className="text-nowrap italic">
                {listWithoutPriceLabel}
              </span>
            )}
            {!listWithoutPrice &&
              (!!campaignPrice?.regularTotalDiscount ? (
                <>
                  <span className="text-nowrap">
                    <span className="pricing-details-row__feeOriginPrice">
                      {formatPrice(basePrice + getBaseFees())}
                    </span>
                  </span>
                  {formatPrice(
                    basePrice +
                      getBaseFees() -
                      campaignPrice.regularTotalDiscount
                  )}
                </>
              ) : (
                <span className="text-nowrap">
                  {formatPrice(basePrice + getBaseFees())}
                </span>
              ))}
          </span>
        </summary>
      )}
      <div className="py-2">
        {!listWithoutPrice && (
          <div>
            <table className="table">
              <tbody>
                <tr
                  className={classNames("pricing-details-row", {
                    "pricing-details-row-hover": highlightPriceRowOnHover,
                  })}
                >
                  <th className="fw-normal">
                    {campaignPrice?.basePriceLabel ?? basePriceLabel}
                  </th>
                  <td className="text-end text-nowrap">
                    {formatPrice(basePrice)}
                  </td>
                </tr>
                <BaseFeesWithCampaignLegacy
                  baseFees={baseFees}
                  campaign={campaignPrice}
                  isWeekendOrHoliday={false}
                  isEvening={false}
                />
              </tbody>
            </table>
            <div className="pricing-details-summary fw-bold px-2">
              <span className="pe-1">{endPriceLabel}</span>
              <span className="text-nowrap">
                {formatPrice(
                  basePrice +
                    getBaseFees() -
                    (campaignPrice?.regularTotalDiscount ?? 0)
                )}
              </span>
            </div>
          </div>
        )}

        <PriceListFeesLegacy
          priceListFees={priceListFees}
          highlightPriceRowOnHover={highlightPriceRowOnHover}
          campaignPrice={campaignPrice}
        />

        {showWeekendEveningPrices &&
          (weekendAndEveningPricesAreEqual ? (
            <>
              <div className="fw-bold mt-2 px-2">
                {campaignPrice
                  ? eveningWeekendDiscountPriceHeading ??
                    eveningWeekendPriceHeading ??
                    ""
                  : eveningWeekendPriceHeading ?? ""}
              </div>
              <table className="table">
                <tbody>
                  <tr
                    className={classNames("pricing-details-row", {
                      "pricing-details-row-hover": highlightPriceRowOnHover,
                    })}
                  >
                    <th className="fw-normal">
                      {eveningWeekendSamePriceLabel ?? ""}
                    </th>
                    <td className="text-end">
                      {formatPrice(
                        eveningAddition +
                          basePrice +
                          getBaseFees() -
                          (campaignPrice?.eveningTotalDiscount ?? 0)
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          ) : (
            !weekendAndEveningPricesAreEqualToBase &&
            (!!eveningAddition || !!weekendHolidayAddition) && (
              <>
                <div className="fw-bold mt-2 px-2">
                  {campaignPrice
                    ? eveningWeekendDiscountPriceHeading ??
                      eveningWeekendPriceHeading ??
                      ""
                    : eveningWeekendPriceHeading ?? ""}
                </div>
                <table className="table">
                  <tbody>
                    {!!eveningAddition && (
                      <tr
                        className={classNames("pricing-details-row", {
                          "pricing-details-row-hover": highlightPriceRowOnHover,
                        })}
                      >
                        <th className="fw-normal">{eveningPriceLabel ?? ""}</th>
                        <td className="text-end">
                          {formatPrice(
                            eveningAddition +
                              basePrice +
                              getBaseFees() -
                              (campaignPrice?.eveningTotalDiscount ?? 0)
                          )}
                        </td>
                      </tr>
                    )}
                    {!!weekendHolidayAddition && (
                      <tr
                        className={classNames("pricing-details-row", {
                          "pricing-details-row-hover": highlightPriceRowOnHover,
                        })}
                      >
                        <th className="fw-normal">{weekendPriceLabel ?? ""}</th>
                        <td className="text-end">
                          {formatPrice(
                            weekendHolidayAddition +
                              basePrice +
                              getBaseFees() -
                              (campaignPrice?.weekendHolidayTotalDiscount ?? 0)
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </>
            )
          ))}

        <FeeWithDiscountTableLegacy
          heading={additionalFeesLabel}
          addHadingMargin
          fees={additionalFees}
          highlightPriceRowOnHover={highlightPriceRowOnHover}
          getPrice={(fee) => fee.regularPrice}
          getDiscount={(fee) => fee.regularDiscount}
        />
      </div>
    </>
  );
};
